import type { FilterValue } from '../types/filter-value.type';

/**
 * Filter scope of a single reconciliation. Travels with every reconciliation
 * SSE event, the BullMQ job payload, and the SSRM `getDimensionGroups`
 * cold-start meta. The frontend evaluates per-row "is this row in scope?"
 * via {@link rowMatchesScope} — no pre-expanded path list is ever shipped.
 *
 * Replaces the previous `uniqueCombinationPaths: string[]` which materialised
 * every affected leaf and aggregate row up-front (200k+ strings, 22+ MB).
 */
export interface ReconciliationScope {
  dimensions: Record<string, FilterValue>;
  resolvedFilter?: Record<string, NonNullable<FilterValue>[]> | null;
}

export function rowMatchesScope(
  row: { dimensions: Record<string, FilterValue> },
  scope: ReconciliationScope,
): boolean {
  for (const key of Object.keys(scope.dimensions)) {
    const scopeValue = scope.dimensions[key];
    const rowValue = row.dimensions[key];
    if (rowValue !== undefined && rowValue !== scopeValue) {
      return false;
    }
  }
  if (scope.resolvedFilter) {
    for (const col of Object.keys(scope.resolvedFilter)) {
      const allowed = scope.resolvedFilter[col];
      if (!allowed || allowed.length === 0) continue;
      const rowValue = row.dimensions[col];
      if (rowValue === undefined || rowValue === null) continue;
      if (!allowed.includes(rowValue)) return false;
    }
  }
  return true;
}

/**
 * Whether a reconciliation can touch any row of a grid filtered by `filter`
 * (the viewer's merged quick and "All filters" selection). A filtered column
 * that the scope pins to another value, or restricts to values the filter
 * does not allow, leaves the two disjoint.
 */
export function scopeIntersectsFilter(
  scope: ReconciliationScope,
  filter: Record<string, readonly FilterValue[]> | null,
): boolean {
  if (!filter) return true;
  for (const [col, allowed] of Object.entries(filter)) {
    if (allowed.length === 0) continue;
    const pinned = scope.dimensions[col];
    if (pinned !== undefined && !allowed.includes(pinned)) return false;
    const restricted = scope.resolvedFilter?.[col];
    if (restricted?.length && !restricted.some((v) => allowed.includes(v))) {
      return false;
    }
  }
  return true;
}

export function scopeKey(scope: ReconciliationScope): string {
  return JSON.stringify({
    dimensions: sortKeys(scope.dimensions),
    resolvedFilter: scope.resolvedFilter
      ? sortKeys(scope.resolvedFilter)
      : null,
  });
}

function sortKeys<T>(obj: Record<string, T>): Record<string, T> {
  const sorted: Record<string, T> = {};
  for (const key of Object.keys(obj).sort()) {
    sorted[key] = obj[key];
  }
  return sorted;
}
